import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, PawPrint, Instagram, Facebook } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { createChatSession } from '../services/geminiService';
import { ChatMessage } from '../types';

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { type: 'bot', text: "Hello! I'm your Natuvía Assistant. Pure Nature, Pure Love. How can I help you today?" }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  const [botAvatar] = useState(() => {
    const avatars = ['🐶', '🐱', '🐰', '🐼', '🐹'];
    return avatars[Math.floor(Math.random() * avatars.length)];
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const chatSessionRef = useRef<any>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isTyping, isOpen]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    const userMsg: ChatMessage = { type: 'user', text: input };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    try {
      if (!chatSessionRef.current) {
         chatSessionRef.current = createChatSession();
      }

      const result = await chatSessionRef.current.sendMessageStream({ message: userMsg.text });
      
      setIsTyping(false);
      setMessages(prev => [...prev, { type: 'bot', text: '' }]);
      
      let fullResponseText = '';
      
      for await (const chunk of result) {
        const chunkText = chunk.text || '';
        const characters = chunkText.split('');

        for (const char of characters) {
          fullResponseText += char;
          
          setMessages(prev => {
             const newMessages = [...prev];
             const lastMsg = newMessages[newMessages.length - 1];
             if (lastMsg.type === 'bot') {
                 lastMsg.text = fullResponseText;
             }
             return newMessages;
          });
          await new Promise(resolve => setTimeout(resolve, 5));
        }
      }

    } catch (error: any) {
      console.error("AI Error:", error);
      setIsTyping(false);
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMsg = newMessages[newMessages.length - 1];
        if (lastMsg && lastMsg.type === 'bot' && lastMsg.text === '') {
            newMessages.pop();
        }
        return [...newMessages, { 
          type: 'bot', 
          text: `I'm having trouble connecting to the network. Error: ${error.message || 'Unknown error'}. Please check Cloudflare Environment Variables.` 
        }];
      });
    }
  };

  const socialLinks = [
    { 
      name: 'Instagram', 
      icon: <Instagram className="w-5 h-5" />, 
      url: 'https://www.instagram.com/natuviapet',
      color: 'bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7]',
      delay: 'delay-[100ms]'
    },
    { 
      name: 'Facebook', 
      icon: <Facebook className="w-5 h-5" />, 
      url: 'https://www.facebook.com/share/17wt6M1jmk/?mibextid=wwXIfr',
      color: 'bg-[#1877F2]',
      delay: 'delay-0'
    }
  ];

  return (
    <>
      {/* 
        Interaction Hub 
        - 固定於右下角
        - 使用 group 類別來觸發內部的 hover 動畫
      */}
      <div 
        className="fixed bottom-5 right-5 z-[9999] flex flex-col items-center group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        
        {/* 社群圖示堆疊 - 僅在懸停且聊天視窗關閉時彈出 */}
        <div className={`flex flex-col gap-3 mb-3 transition-all duration-500 ease-out ${
          isHovered && !isOpen 
            ? 'translate-y-0 opacity-100 pointer-events-auto' 
            : 'translate-y-10 opacity-0 pointer-events-none'
        }`}>
          {socialLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`p-3 rounded-full text-white shadow-lg transition-all duration-300 hover:scale-110 active:scale-95 ${link.color} ${link.delay}`}
              title={`Follow us on ${link.name}`}
            >
              {link.icon}
            </a>
          ))}
        </div>

        {/* Chatbot 主按鈕 */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className={`relative p-4 rounded-full shadow-[0_8px_32px_rgba(0,0,0,0.3)] backdrop-blur-md border border-white/20 transition-all duration-300 ${
            isOpen 
              ? 'rotate-90 bg-white text-gray-600 opacity-100 scale-100' 
              : 'bg-[#4f573e] text-white opacity-70 group-hover:opacity-100 group-hover:scale-110 active:scale-95'
          }`}
          aria-label="Open Chat"
        >
          {isOpen ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
          
          {/* 未展開時的小紅點 - 提示在線狀態 */}
          {!isOpen && !isHovered && (
            <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 border-2 border-white rounded-full"></span>
          )}
        </button>
      </div>

      {/* 聊天視窗 */}
      <div 
        className={`fixed bottom-24 right-5 left-5 md:left-auto md:w-[380px] bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.2)] border border-white/20 z-[9999] flex flex-col overflow-hidden transition-all duration-500 origin-bottom-right ${
          isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-75 opacity-0 translate-y-10 pointer-events-none'
        }`} 
        style={{ maxHeight: 'min(650px, 80vh)' }}
      >
        {/* Header */}
        <div className="bg-[#4f573e] p-6 flex items-center justify-between border-b border-white/10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-3xl shadow-sm border border-white/10">
              {botAvatar}
            </div>
            <div>
              <h3 className="text-white font-black text-base tracking-tight">Assistant</h3>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-green-300 text-[10px] font-black uppercase tracking-widest">Online</span>
              </div>
            </div>
          </div>
          <button onClick={() => setIsOpen(false)} className="text-white/50 hover:text-white bg-white/5 p-2 rounded-xl"><X className="w-5 h-5" /></button>
        </div>

        {/* 訊息區域 */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-[#fffcf8] min-h-[300px] custom-scrollbar" ref={scrollRef}>
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] p-4 rounded-3xl text-sm leading-relaxed shadow-sm ${
                msg.type === 'user' ? 'bg-natuvia-brown text-white rounded-tr-none' : 'bg-white text-gray-700 rounded-tl-none border border-gray-100'
              }`}>
                {msg.type === 'user' ? <p className="font-bold">{msg.text}</p> : 
                  <div className="prose prose-sm max-w-none prose-p:leading-relaxed">
                    <ReactMarkdown>{msg.text}</ReactMarkdown>
                  </div>
                }
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start">
              <div className="bg-white p-4 rounded-3xl rounded-tl-none border border-gray-100 flex gap-1.5 shadow-sm">
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"></span>
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></span>
                <span className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce" style={{animationDelay: '0.4s'}}></span>
              </div>
            </div>
          )}
        </div>

        {/* 輸入區域 */}
        <div className="p-4 bg-white border-t border-gray-100 flex gap-3">
          <input 
            type="text" value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type message..."
            className="flex-1 bg-gray-50 rounded-2xl px-5 py-3 text-sm focus:outline-none transition-all"
          />
          <button onClick={handleSend} disabled={!input.trim() || isTyping} className="p-3 bg-[#4f573e] text-white rounded-2xl disabled:opacity-30"><PawPrint className="w-5 h-5" /></button>
        </div>
      </div>
    </>
  );
};

export default ChatWidget;